import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProgressWizard from 'components/field/ProgressWizard';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalTitle } from 'components/global/styled/Modal.styled';

import useAppTracking from 'hooks/useAppTracking';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';
import PaymentModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptonElement/PaymentModal';
import useBillingDetailsValidation from 'pages/Settings/Payment/hooks/useBillingValidation';

import { PAYMENT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faCheck, faCreditCard, faFileInvoice } from '@fortawesome/pro-solid-svg-icons';

import {
	clearStates,
	getSubscriptionDiscountedAmount,
	setSubscriptionRetryPaymentAmount
} from './SubscriptionAccountSlice';
import BillingInformation from './SubscriptonElement/BillingInformation';
import CompletedModal from './SubscriptonElement/CompletedModal';
import ContactUsModal from './SubscriptonElement/ContactUsModal';
import DowngradeConfirmationModal from './SubscriptonElement/DowngradeConfirmationModal';
import PackageSelectionModal from './SubscriptonElement/PackageSelectionModal';
import UpgradeConfirmationModal from './SubscriptonElement/UpgradeConfirmationModal';

/**
 * @author @muhammedburakak
 */

const TABS = {
	paymentMethod: { name: 'subscription.payment', icon: faCreditCard },
	packageSelection: { name: 'subscription.packageSelection', icon: faCheck },
	billingInfo: { name: 'subscription.billingInfo', icon: faFileInvoice },
	upgradeConfirmation: { name: 'subscription.upgradeConfirmation', icon: faCheck },
	downgradeConfirmation: { name: 'subscription.downgradeConfirmation', icon: faCheck },
	contactUs: { name: 'subscription.contactUs', icon: faCheck },
	completed: { name: 'subscription.completed', icon: faCheck, success: true }
};

const SubscriptionAccountModal = NiceModal.create(
	({
		modalName,
		currentPackage: currentPackageProp,
		selectedPackage: selectedPackageProp,
		selectedPlanType,
		manageSubscription,
		renewSubscription
	}) => {
		// Hooks and states declaration
		const { closeModal } = useModalRouter();
		const axiosPrivate = useAxiosPrivate();
		const isMobile = useMediaQuery(Breakpoints.mobile);
		const modal = useModal();
		const dispatch = useDispatch();
		const { track } = useAppTracking();
		const { isBillingValid } = useBillingDetailsValidation();

		const { accountSuspended, planName: currentPlanName } = useSelector((state) => state?.user);

		const subscriptionPromoCodeCheck = useSelector((state) => state.subscriptionAccount.subscriptionPromoCodeCheck);
		const { otoPackageList: packages } = useSelector((state) => state?.otoPackageList);
		const user = useSelector((state) => state.user);

		const [planType, setPlanType] = useState((selectedPlanType || user.subscriptionPaymentType) ?? 'monthly');
		const [currentPackage, setCurrentPackage] = useState(
			currentPackageProp ?? packages.find((item) => item.planName === currentPlanName)
		);
		const [selectedPackage, setSelectedPackage] = useState(
			manageSubscription || renewSubscription ? currentPackage : selectedPackageProp
		);

		const isDowngrade = useMemo(() => {
			if (selectedPackage?.tier === 1 && accountSuspended) {
				return true;
			}
			return (
				(!accountSuspended && currentPackage?.tier > selectedPackage?.tier) ||
				(currentPackage?.tier === selectedPackage?.tier &&
					user.subscriptionPaymentType === 'YEARLY' &&
					planType === 'monthly')
			);
		}, [selectedPackage, accountSuspended, currentPackage, user]);

		const [selectedIndex, setSelectedIndex] = useState(renewSubscription || manageSubscription ? 0 : 1);

		useEffect(() => {
			dispatch(getOTOPackageList());

			return () => {
				dispatch(clearStates(null));
			};
		}, []);

		useUpdateEffect(() => {
			const currentPackageTemp = currentPackageProp ?? packages.find((item) => item.planName === currentPlanName);
			setCurrentPackage(currentPackageTemp);
			setSelectedPackage(manageSubscription || renewSubscription ? currentPackageTemp : selectedPackageProp);
		}, [packages, currentPlanName]);

		useEffect(() => {
			if (renewSubscription) getRetrySubscriptionAmount();
		}, [renewSubscription]);

		useEffect(() => {
			if (selectedPackage?.tier !== 4 && selectedPackage && subscriptionPromoCodeCheck) getDiscountedAmount();
		}, [subscriptionPromoCodeCheck]);

		const handleCloseEvents = () => {
			if (!isDowngrade) {
				if (subscriptionTabs[selectedIndex].name === TABS.completed.name) {
					return;
				}

				if (selectedPackage?.tier === 2) {
					track('starterClosingUpgradePopup');
				} else if (selectedPackage?.tier === 3) {
					track('scaleClosingUpgradePopup');
				} else if (selectedPackage?.tier === 4) {
					track('enterpriseClosingUpgradePopup');
				}
			}
		};

		const handleCloseModal = () => {
			handleCloseEvents();
			if (modalName) {
				modal.resolve({
					showOTOModuleMessage:
						selectedPackage.planName !== 'EnterprisePackage' &&
						subscriptionTabs[selectedIndex]?.name === TABS.completed.name
				});
				closeModal();
			} else {
				!isDowngrade &&
					modal.resolve({
						showOTOModuleMessage:
							selectedPackage.planName !== 'EnterprisePackage' &&
							subscriptionTabs[selectedIndex]?.name === TABS.completed.name
					});
				modal.remove();
			}
		};

		useEffect(() => {
			if (import.meta.env.VITE_PLATFORM === 'desktop' && modal.visible) {
				if (modal.visible) {
					handleCloseModal();
				}
				window?.electronAPI?.openExternalBrowser?.(
					`https://${import.meta.env.VITE_ENVIRONMENT === 'production' ? 'app' : 'staging-api'}.tryoto.com/oto-modules&oto-token=${localStorage.getItem('ACCESS_TOKEN')}`
				);
			}
		}, [modal.visible]);

		// The amount to be paid by the user is obtained.
		const getDiscountedAmount = useCallback(async () => {
			dispatch(
				getSubscriptionDiscountedAmount({
					paymentType: planType.toUpperCase(),
					newPackageID: selectedPackage?.id,
					oldPackageID: !accountSuspended ? currentPackage?.id : undefined,
					promoCode: subscriptionPromoCodeCheck || undefined
				})
			);
		}, [planType, selectedPackage, currentPackage, subscriptionPromoCodeCheck]);

		const getRetrySubscriptionAmount = useCallback(async () => {
			try {
				const response = await axiosPrivate.post(PAYMENT.GET_RETRY_PAYMENT_AMOUNT);

				if (response.status === 200) {
					dispatch(setSubscriptionRetryPaymentAmount(response.data));
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
			}
		}, [renewSubscription]);

		const subscriptionTabs = useMemo(() => {
			if (isDowngrade) {
				return [TABS.packageSelection, TABS.downgradeConfirmation];
			} else if (renewSubscription) {
				return [TABS.paymentMethod, TABS.completed];
			} else if (selectedPackage?.tier === 4) {
				return [TABS.packageSelection, TABS.contactUs];
			}
			// Add billing info tab if billing details are not valid
			return !isBillingValid
				? [
						TABS.packageSelection,
						TABS.upgradeConfirmation,
						TABS.billingInfo,
						TABS.paymentMethod,
						TABS.completed
					]
				: [TABS.packageSelection, TABS.upgradeConfirmation, TABS.paymentMethod, TABS.completed];
		}, [isDowngrade, selectedPackage, isBillingValid]);

		const renderTab = (index) => {
			const tab = subscriptionTabs[index];
			switch (tab.name) {
				case TABS.paymentMethod.name:
					return (
						<PaymentModal
							closeModal={handleCloseModal}
							nextStep={nextStep}
							prevStep={prevStep}
							renewSubscription={renewSubscription}
							subscription={!!selectedPackage && !renewSubscription}
							selectedPackage={selectedPackage}
							currentPackage={currentPackage}
							planType={planType}
						/>
					);
				case TABS.billingInfo.name:
					return <BillingInformation closeModal={handleCloseModal} prevStep={prevStep} nextStep={nextStep} />;
				case TABS.downgradeConfirmation.name:
					return (
						<DowngradeConfirmationModal
							selectedPackage={selectedPackage}
							currentPackage={currentPackage}
							closeModal={handleCloseModal}
							prevStep={prevStep}
							planType={planType}
						/>
					);
				case TABS.upgradeConfirmation.name:
					return (
						<UpgradeConfirmationModal
							selectedPackage={selectedPackage}
							currentPackage={currentPackage}
							closeModal={handleCloseModal}
							nextStep={nextStep}
							prevStep={prevStep}
							planType={planType}
						/>
					);
				case TABS.packageSelection.name:
					return (
						<PackageSelectionModal
							closeModal={handleCloseModal}
							nextStep={handlePackageSelectionNextStep}
							currentPackage={currentPackage}
							selectedPackage={selectedPackage}
							setSelectedPackage={setSelectedPackage}
							planType={planType}
							setPlanType={setPlanType}
						/>
					);
				case TABS.completed.name:
					return (
						<CompletedModal
							closeModal={handleCloseModal}
							prevStep={prevStep}
							renewSubscription={renewSubscription}
							subscription={!!selectedPackage && !renewSubscription}
						/>
					);
				case TABS.contactUs.name:
					return (
						<ContactUsModal
							closeModal={handleCloseModal}
							prevStep={prevStep}
							selectedPackage={selectedPackage}
						/>
					);
				default:
					return null;
			}
		};

		const nextStep = () => {
			if (selectedIndex + 1 < subscriptionTabs.length) setSelectedIndex(selectedIndex + 1);
		};

		const prevStep = () => {
			if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
		};

		useEffect(() => {
			if (renewSubscription) {
				return;
			}
			getDiscountedAmount();
		}, [renewSubscription]);

		const handlePackageSelectionNextStep = () => {
			if (selectedPackage?.tier === 4 || isDowngrade) {
				nextStep();
				return;
			}
			getDiscountedAmount();
			nextStep();
		};

		return (
			<Modal name={modalName} fullwidth className="modal-subscriptionAccount" onClose={handleCloseEvents}>
				<ModalTitle>
					<Typography variant="p1">
						{renewSubscription ? 'renewSubscription' : 'chargeAccount.changePlan'}
					</Typography>
				</ModalTitle>

				<ProgressWizard
					className={isMobile ? 'mt-4' : ''}
					stages={subscriptionTabs}
					index={selectedIndex}
					setIndex={setSelectedIndex}
					gutterBottom
				/>

				{currentPackage ? <>{renderTab(selectedIndex)}</> : <Loading />}
			</Modal>
		);
	}
);

export default SubscriptionAccountModal;
